<script setup lang="ts">
    /**
     * ANVÄND INTE FÖR K-KOMPONENTER. Används bara för <formulateinout> i template.
     *
     * KInputWrapper används som en wrapper för formulate input.
     *
     * Den hookar in i store och kollar om fältet ska synas eller inte. "name" som skickas in som prop kan kommas åt
     * via v-slot props som "inputName" för att slippa dubbel hårdkodning.
     *
     * Om groupName skickas med så kommer det göras en check mot att den gruppen ska synas. Fältet kommer att döljas om
     * gruppen inte ska synas.
     *
     * Exempel på användning:
     * <KInputWrapper name="patientnamn" v-slot="{ inputName, shouldBeVisible }">
     *     <FormulateInput
     *         v-if="shouldBeVisible"
     *         :name="inputName"
     *     />
     * </KInputWrapper>
     */

    import { CONFIG_PROVIDE_NAME, ConfigType } from "@/store/modules/types/configModuleTypes";
    import { computed, inject, toRefs } from "vue";
    import { useStore } from "@/store";

    const store = useStore();

    interface Props {
        /**
         * Fältets namn
         */
        name: string;
        /**
         * Namn på gruppen i inställningar som ska kollas mot
         */
        groupName?: string;
        required?: boolean;
    }

    const props = defineProps<Props>();

    const { name, groupName, required } = toRefs(props);

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>(CONFIG_PROVIDE_NAME);

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
            type: ConfigType.Field,
        })
    );

    const shouldBeRequired = computed(() =>
        store.getters["config/shouldBeRequiredByName"]({
            defaultRequired: required?.value,
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
        })
    );

    const groupShouldBeVisible = computed(() => {
        if (groupName?.value) {
            return store.getters["config/shouldBeVisibleByName"]({
                name: groupName.value,
                moduleName: configNames?.moduleName,
                subModuleName: configNames?.subModuleName,
                type: ConfigType.Group,
            });
        }

        return true;
    });
</script>

<template>
    <div class="k-input-wrapper">
        <slot
            :shouldBeVisible="shouldBeVisible && groupShouldBeVisible"
            :inputName="name"
            :shouldBeRequired="shouldBeRequired"
        />
    </div>
</template>

<style lang="scss" scoped>
    .k-input-wrapper {
        display: contents;
    }
</style>
